<script setup lang="ts">
import { computed, ref, useAttrs,watch } from 'vue'

import { IconField, InputIcon } from 'primevue'
import DatePicker from 'primevue/datepicker'
import FloatLabel from 'primevue/floatlabel'

type DatePickerView = 'date' | 'month' | 'year'
type DatePickerSelectionMode = 'single' | 'multiple' | 'range'

interface Props {
  modelValue?: Date| Date[] | null
  label?: string
  placeholder?: string
  dateFormat?: string
  errorMessage?: string
  customClass?: string
  showTime?: boolean
  disabled?: boolean
  readonly?: boolean
  size?: 'small' | 'large'
  minDate?: Date
  maxDate?: Date
  view?: DatePickerView
  selectionMode?: DatePickerSelectionMode
  numberOfMonths?: number
  inline?: boolean
  hourFormat?: '12' | '24'
  showSeconds?: boolean
  showButtonBar?: boolean
  showClear?: boolean
  containerClass?: string
  inputClass?: string
  errorClass?: string
  inputId?: string
  pt?: object
  ptOptions?: object
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: '',
  placeholder: ' ', // Espacio en blanco para que FloatLabel funcione correctamente
  showIcon: false,
  dateFormat: 'dd/mm/yy',
  errorMessage: '',
  customClass: '',
  showTime: false,
  disabled: false,
  readonly: false,
  size: 'small',
  view: 'date',
  selectionMode: 'single',
  numberOfMonths: 1,
  inline: false,
  hourFormat: '24',
  showSeconds: false,
  showButtonBar: false,
  showClear: true,
  containerClass: 'w-full',
  inputClass: '!w-full !rounded-lg !text-xs py-2! app-card-glass dark:bg-transparent! shadow-sm! !border-white/60 dark:!border-[var(--p-primary-color)]/40 hover:!border-white dark:hover:!border-[var(--p-primary-color)]/60 hover:!shadow-md',
  errorClass: 'text-red-400 dark:text-red-300 p-0 m-0'
})

defineOptions({
  inheritAttrs: false
})

const emit = defineEmits<{
  'update:modelValue': [value: Date | Date[] | null]
}>()

const selectedDate = ref<Date | Date[] | null>(null)

// Placeholder efectivo para FloatLabel
/* const effectivePlaceholder = computed(() => {
  // Si el placeholder está vacío o es solo espacios, usar un espacio para FloatLabel
  return props.placeholder?.trim() === '' ? ' ' : props.placeholder
}) */

// Filtrar atributos conflictivos para evitar que sobrescriban props controlados
const blockKeys = [
  'modelValue', 'onUpdate:modelValue',
  'showIcon', 'iconDisplay',
  'inputId', 'placeholder',
  'inputClass', 'pt'
]

// ID único para el input
const inputId = computed(() => props.inputId || `app-datepicker-${crypto.randomUUID()}`)

const attrs = useAttrs()

const forwardedAttrs = computed(() => {
  const src = (attrs ?? {}) as Record<string, unknown>
  // Filtrar tanto las claves bloqueadas como 'class' que manejamos por separado
  return Object.fromEntries(Object.entries(src).filter(([k]) => !blockKeys.includes(k) && k !== 'class'))
})

const ptMerged = computed(() => ({
  panel: {
    class:
      'app-date-picker-glass-panel app-card-glass backdrop-blur-sm rounded-xl shadow-xl overflow-hidden'
  },
  calendarContainer: { class: 'bg-transparent!' },
  calendar: { class: 'bg-transparent!' },
  header: { class: 'bg-transparent! border-0!' },
  dayView: { class: 'bg-transparent!' },
  ...(props.pt as object || {})
}))

// Separar clases del contenedor vs clases del input
const containerClasses = computed(() => {
  const classAttr = attrs.class as string | undefined
  return classAttr ? `${props.containerClass} ${classAttr}` : props.containerClass
})

watch(
  () => props.modelValue,
  (newValue) => {
    selectedDate.value = newValue ?? null
  },
  { immediate: true }
)

// let previousValue: Date | null = selectedDate.value

watch(
  selectedDate,
  (value) => {
    emit('update:modelValue', value)
  },
  { deep: true }
)
</script>
<template>
  <div :class="containerClasses">
    <FloatLabel variant="on" v-if="$slots.icon">
      <IconField>
        <InputIcon>
          <slot name="icon" />
        </InputIcon>
        <DatePicker
          v-bind="forwardedAttrs"
          :model-value="selectedDate"
          :date-format="dateFormat"
          :show-time="showTime"
          :disabled="disabled"
          :readonly="readonly"
          :size="size"
          :min-date="minDate"
          :max-date="maxDate"
          :view="view"
          :selection-mode="selectionMode"
          :number-of-months="numberOfMonths"
          :inline="inline"
          :hour-format="hourFormat"
          :show-seconds="showSeconds"
          :show-button-bar="showButtonBar"
          :show-clear="showClear"
          :pt="ptMerged"
          :pt-options="ptOptions"
          :show-icon="false"
          icon-display="input"
          :input-class="inputClass"
          :class="[
            '!w-full',
            customClass,
            { 'p-invalid': errorMessage.length > 0 }
          ]"
          :input-id="inputId"
          @update:model-value="selectedDate = $event as Date | null"
        />
      </IconField>
      <label class="text-xs 2xl:text-sm">{{ label }}</label>
    </FloatLabel>

    <FloatLabel variant="on" v-else>
      <DatePicker
        v-bind="forwardedAttrs"
        :model-value="selectedDate"
        :date-format="dateFormat"
        :show-icon="false"
        icon-display="input"
        :show-time="showTime"
        :disabled="disabled"
        :readonly="readonly"
        :size="size"
        :min-date="minDate"
        :max-date="maxDate"
        :view="view"
        :selection-mode="selectionMode"
        :number-of-months="numberOfMonths"
        :inline="inline"
        :hour-format="hourFormat"
        :show-seconds="showSeconds"
        :show-button-bar="showButtonBar"
        :show-clear="showClear"
        :pt="ptMerged"
        :pt-options="ptOptions"
        :input-class="inputClass"
        :class="[
          '!w-full',
          customClass,
          { 'p-invalid': errorMessage.length > 0 }
        ]"
        :input-id="inputId"
        @update:model-value="selectedDate = $event as Date | null"
      />
      <label class="text-xs 2xl:text-sm">{{ label }}</label>
    </FloatLabel>

    <div v-if="errorMessage.length" :class="errorClass">
      <small>{{ errorMessage }}</small>
    </div>
  </div>
</template>

<style scoped>
:deep(.p-floatlabel:not(:has(.p-datepicker.p-focus)):not(:has(.p-datepicker-open)):not(:has(.p-inputtext:focus)):not(:has(.p-inputwrapper-focus)):not(:has(.p-invalid)) > label) {
  color: var(--p-surface-400);
}

:deep(.p-inputtext:not(:focus)::placeholder) {
  color: var(--p-surface-400);
  opacity: 1;
}

:global(.dark) :deep(.p-floatlabel:not(:has(.p-datepicker.p-focus)):not(:has(.p-datepicker-open)):not(:has(.p-inputtext:focus)):not(:has(.p-inputwrapper-focus)):not(:has(.p-invalid)) > label) {
  color: var(--p-surface-300);
}

:global(.dark) :deep(.p-inputtext:not(:focus)::placeholder) {
  color: var(--p-surface-300);
}

:global(.app-date-picker-glass-panel) {
  background: color-mix(in srgb, var(--p-surface-0) 95%, transparent) !important;
  border-color: color-mix(in srgb, var(--p-surface-0) 50%, transparent);
}

:global(.dark .app-date-picker-glass-panel) {
  background: color-mix(in srgb, var(--p-primary-950) 42%, transparent) !important;
}

:global(.app-date-picker-glass-panel .p-datepicker-calendar-container),
:global(.app-date-picker-glass-panel .p-datepicker-calendar),
:global(.app-date-picker-glass-panel .p-datepicker-header),
:global(.app-date-picker-glass-panel .p-datepicker-day-view),
:global(.app-date-picker-glass-panel .p-datepicker-month-view),
:global(.app-date-picker-glass-panel .p-datepicker-year-view) {
  background: transparent !important;
}

:global(.app-date-picker-glass-panel .p-datepicker-day-cell),
:global(.app-date-picker-glass-panel .p-datepicker-day:not(.p-datepicker-day-selected)) {
  background: transparent !important;
}



:global(.app-date-picker-glass-panel .p-datepicker-prev-button),
:global(.app-date-picker-glass-panel .p-datepicker-next-button) {
  background: transparent !important;
  color: var(--p-primary-color) !important;
}
</style>
